import React from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getFintoc } from '@fintoc/fintoc-js';
import AlertDialog from '../../AlertDialog';
import { sxStyles } from '../styles';
import {
  postExternalSubscriptionIntent,
  postSwitchSubscription,
  getLoan,
} from '../../../queries/account';
import { ExternalSubscriptionItem } from '../../../types/account';
import {
  FintocMetadataDetails, GetnetMetadataDetails, MetadataDetails, BasePaymentMethod,
} from '../../../types/authorization';
import fintocLogo from '../../../assets/fintoc_icon.jpg';
import analytics from '../../../utils/analytics';
import getFintocGTMEventLabel from './helpers';

const subscribedStates = ['PENDING', 'ACTIVE'];

interface ChangePacDialogProps {
  button: React.ReactElement;
  subscriptions: ExternalSubscriptionItem[];
  primarySubscription: ExternalSubscriptionItem | undefined;
  token: string;
  idNumber: string;
  paymentMethodProvider: 'fintoc' | 'etpay' | 'getnet';
  onSwitchSuccess: () => Promise<void>;
  onNewSubscriptionSuccess: (subscriptionId?: string) => Promise<void>;
  onDeleteSubscription?: (subscriptionId: string) => Promise<void>;
}

export function formatAccountSummary(
  metadata: MetadataDetails,
  paymentMethod: BasePaymentMethod,
): string {
  if (paymentMethod === 'pac') {
    const m = metadata as FintocMetadataDetails;
    return [m.institutionName || m.bank || '', m.accountNumber ? `···${m.accountNumber.slice(-4)}` : ''].filter(Boolean).join(' · ');
  }
  if (paymentMethod === 'pat') {
    const m = metadata as GetnetMetadataDetails;
    return [m.franchiseName || '', m.issuerName || '', m.lastDigits ? `*** **** ${m.lastDigits}` : ''].filter(Boolean).join(' · ');
  }
  return `Suscripción ${paymentMethod.toUpperCase()}`;
}

export function formatSubscriptionDate(dateStr?: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function ChangePacDialog({
  button,
  primarySubscription,
  subscriptions,
  token,
  idNumber,
  paymentMethodProvider,
  onSwitchSuccess,
  onNewSubscriptionSuccess,
  onDeleteSubscription,
}: ChangePacDialogProps) {
  const theme = useTheme();
  const { loanData, loanDataLoading } = getLoan(token, true);
  const [selectedId, setSelectedId] = React.useState(primarySubscription?.id || '');
  const [switchLoading, setSwitchLoading] = React.useState(false);
  const [cooldownError, setCooldownError] = React.useState<string | null>(null);
  const [newSubLoading, setNewSubLoading] = React.useState(false);
  const [closeTrigger, setCloseTrigger] = React.useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(null);

  const sortedSubscriptions = React.useMemo(() => {
    const primary = subscriptions.filter((s) => s.isPrimary);
    const nonPrimary = [...subscriptions.filter((s) => !s.isPrimary && subscribedStates.includes(s.subscriptionStatus || ''))].sort(
      (a, b) => new Date(b.lastChangedAt || b.createdAt).getTime()
        - new Date(a.lastChangedAt || a.createdAt).getTime(),
    );
    return [...primary, ...nonPrimary];
  }, [subscriptions]);

  const mostRecentNonPrimary = sortedSubscriptions.find((s) => !s.isPrimary);

  const handleSwitch = async () => {
    if (!selectedId) return;
    setSwitchLoading(true);
    setCooldownError(null);
    try {
      await postSwitchSubscription(token, selectedId);
      await onSwitchSuccess();
    } catch {
      setCooldownError('Ha ocurrido un error, por favor contáctanos');
    } finally {
      setSwitchLoading(false);
    }
  };

  const handleSubscribeNew = async () => {
    setNewSubLoading(true);
    analytics.page('PU - PAGOS', 'SUSCRIPCION NUEVO PAC');
    setCooldownError(null);
    let secondFactor: string | null = null;

    try {
      const res = await postExternalSubscriptionIntent(token, paymentMethodProvider as 'fintoc' | 'etpay');
      if (!res.subscriptionId) {
        setNewSubLoading(false);
        return;
      }
      const Fintoc = await getFintoc();
      const widget = await Fintoc?.create({
        widgetToken: res.widgetToken,
        holderType: 'individual',
        product: 'subscriptions',
        publicKey: process.env.REACT_APP_FINTOC_PUBLIC_KEY,
        username: ['dev', 'beta', 'localhost'].some((env) => process.env.REACT_APP_API_URL?.includes(env))
          ? undefined
          : { value: idNumber, editable: false },
        onExit: () => {
          setNewSubLoading(false);
        },
        onSuccess: async (event: {
          subscription: { account: { institution: { name: string } } };
        }) => {
          analytics.track('Nuevo PAC Suscrito', {
            initiator: 'usuario',
            paymentMethod: paymentMethodProvider,
            bank: event.subscription.account.institution.name,
            flow: 'change',
          });
          onNewSubscriptionSuccess(res.subscriptionId);
          setCloseTrigger(true);
        },
        onEvent: async (event: string) => {
          if (event === 'on_error') {
            analytics.page('PU - PAGOS', `NUEVO PAC ${getFintocGTMEventLabel(event)}`, {
              paymentMethod: 'fintoc',
            });
            return null;
          }
          if (event === 'closed') {
            analytics.track('Cierre Dialog', {
              initiator: 'usuario',
              paymentMethod: 'fintoc',
            });
            return null;
          }
          if (event.includes('authentication_required')) {
            const sf = event.split('_')[0];
            secondFactor = sf;
          }
          if (event === 'subscription_created') {
            analytics.track('Nuevo PAC Suscrito', {
              initiator: 'usuario',
              authenticationMethod: secondFactor,
              paymentMethod: 'fintoc',
            });
          }
          analytics.page('PU - PAGOS', `NUEVO PAC ${getFintocGTMEventLabel(event)}`, {
            paymentMethod: 'fintoc',
          });
          return null;
        },
      });

      setNewSubLoading(false);
      widget?.open();
    } catch (e) {
      if (e === 422 || e === 'ERR_PAC_UPCOMING_CHARGE_BLOCK') {
        setCooldownError('Tu próximo cobro es en menos de 5 días hábiles, no podrás suscribir un nuevo PAC hasta entonces.');
      } else { setCooldownError('Ha ocurrido un error al iniciar la suscripción'); }
      setNewSubLoading(false);
    }
  };

  const onClose = () => {
    if (primarySubscription) {
      setSelectedId(primarySubscription.id);
    }
    setCloseTrigger(false);
    setCooldownError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId || !onDeleteSubscription) return;
    await onDeleteSubscription(deleteConfirmId);
    analytics.page('PU - PAGOS', 'CUENTA ELIMINADA');
    setDeleteConfirmId(null);
  };

  React.useEffect(() => {
    if (primarySubscription) {
      setSelectedId(primarySubscription.id);
    }
  }, [subscriptions]);

  const lastChangedAt = primarySubscription?.lastChangedAt;
  const fiveDaysAgo = new Date().getTime() - 1000 * 60 * 60 * 24 * 5;
  const canChange = !lastChangedAt
    || (lastChangedAt && new Date(lastChangedAt).getTime() < fiveDaysAgo);

  const hasActiveLoan = loanDataLoading || !!(loanData && Object.keys(loanData).length > 0);

  return (
    <AlertDialog
      button={button}
      maxWidth="xs"
      hideCloseButton="corner"
      hideSubmitButton={!canChange}
      submitButtonLabel="Confirmar cambio"
      submitButtonDisabled={!selectedId || newSubLoading || selectedId === primarySubscription?.id}
      closeAfterSubmit
      submitButtonMaxWidth="100%"
      action={handleSwitch}
      pageCall={{ category: 'PU - PAGOS', name: 'CAMBIAR CUENTA' }}
      triggerClose={closeTrigger}
      onCloseAction={onClose}
      dialogContent={(
        <>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={sxStyles.sectionTitle}
          >
            Cuentas guardadas
          </Typography>

          <RadioGroup
            value={selectedId}
            onChange={(e) => {
              setSelectedId(e.target.value);
              setCooldownError(null);
            }}
          >
            {sortedSubscriptions.map((sub) => {
              const isActive = sub.isPrimary;
              const meta = sub.metadataDetails as FintocMetadataDetails;
              const label = formatAccountSummary(meta, sub.paymentMethod) || 'Cuenta guardada';
              const isLatestNonPrimary = !isActive && sub === mostRecentNonPrimary;
              // eslint-disable-next-line no-nested-ternary
              const subtitle = isActive
                ? 'Cuenta activa actualmente'
                : isLatestNonPrimary && sub.lastChangedAt
                  ? `Última cuenta usada · ${formatSubscriptionDate(sub.lastChangedAt)}`
                  : formatSubscriptionDate(sub.lastChangedAt || sub.createdAt);

              return (
                <Box
                  key={sub.id}
                  sx={{
                    ...sxStyles.subscriptionCard,
                    border: `1.5px solid ${
                      selectedId === sub.id ? theme.palette.secondary.main : '#e0e0e0'
                    }`,
                    backgroundColor:
                        selectedId === sub.id
                          ? alpha(theme.palette.secondary.main, 0.05)
                          : 'white',
                    cursor: isActive ? 'default' : 'pointer',
                  }}
                  onClick={() => !isActive && setSelectedId(sub.id)}
                >
                  <FormControlLabel
                    value={sub.id}
                    control={(
                      <Radio
                        disabled={isActive || !canChange}
                        color="secondary"
                        size="small"
                      />
                      )}
                    label={(
                      <Box sx={sxStyles.subscriptionLabelBox}>
                        <Typography variant="body2" fontWeight={isActive ? 'normal' : 'medium'}>
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subtitle}
                        </Typography>
                      </Box>
                      )}
                    sx={sxStyles.formControlLabel}
                  />
                  {isActive && (
                  <Chip
                    label="Activa"
                    color="primary"
                    size="medium"
                    sx={sxStyles.activeChip}
                  />
                  )}
                  {onDeleteSubscription && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirmId(sub.id);
                    }}
                    sx={{
                      ...sxStyles.deleteIconButton,
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                      },
                    }}
                    aria-label="Eliminar cuenta"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  )}
                </Box>
              );
            })}
          </RadioGroup>

          {canChange && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                o agrega una nueva
              </Typography>
            </Divider>

            <Button
              variant="outlined"
              fullWidth
              sx={{
                ...sxStyles.newSubButton,
                '&:hover': {
                  borderStyle: 'dashed',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                },
              }}
              onClick={handleSubscribeNew}
              disabled={newSubLoading || switchLoading}
            >
              {newSubLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Box sx={sxStyles.newSubButtonContent}>
                  <object
                    data={fintocLogo}
                    style={{ height: 20, pointerEvents: 'none', borderRadius: '25%' }}
                    aria-label="fintoc"
                  />
                  <Typography variant="body2">Suscribir nuevo PAC</Typography>
                </Box>
              )}
            </Button>
          </>
          )}

          {!cooldownError && (
            <Box sx={sxStyles.warningBox}>
              <WarningAmberIcon sx={sxStyles.warningIcon} />
              {!canChange ? (
                <Typography component="div" variant="caption" sx={sxStyles.warningText}>
                  No puedes cambiar tu cuenta
                  {' '}
                  <strong>
                    {primarySubscription?.paymentMethod.toUpperCase()}
                  </strong>
                  {' '}
                  en este momento, debes
                  {' '}
                  <strong>esperar</strong>
                  {' '}
                  al menos
                  {' '}
                  <strong>7 días desde el último cambio.</strong>
                </Typography>
              ) : (
                <Typography component="div" variant="caption" sx={sxStyles.warningText}>
                  Si tu próximo cobro es en menos de
                  {' '}
                  <strong>5 días hábiles</strong>
                  , no podrás suscribir un nuevo PAC.
                </Typography>
              )}
            </Box>
          )}

          {cooldownError && (
            <Alert severity="error" sx={sxStyles.cooldownAlert}>
              {cooldownError}
            </Alert>
          )}
          <AlertDialog
            isTriggered={!!deleteConfirmId}
            onCloseAction={() => setDeleteConfirmId(null)}
            hideLogo
            maxWidth="xs"
            dialogTitle={(
              <Typography variant="body1" color="primary" fontWeight="bold" gutterBottom sx={sxStyles.deleteDialogTitle}>
                ¿Eliminar esta cuenta?
              </Typography>
            )}
            dialogContent={(
              <Typography variant="body2">
                { hasActiveLoan ? (
                  <>
                    No puedes eliminar una cuenta
                    {' '}
                    <Typography component="span" variant="body2" fontWeight="bold" color="secondary">PAC</Typography>
                    {' '}
                    o
                    {' '}
                    <Typography component="span" variant="body2" fontWeight="bold" color="secondary">PAT</Typography>
                    {' '}
                    mientras tengas un crédito activo.
                  </>
                )
                  : (
                    <>
                      Esta cuenta será eliminada de tus métodos de pago guardados.
                      {' '}
                      <strong>Esta acción no puede deshacerse.</strong>
                    </>
                  )}
              </Typography>
            )}
            submitButtonLabel="Eliminar"
            closeButtonLabel="Cancelar"
            submitButtonDisabled={hasActiveLoan}
            closeAfterSubmit
            action={handleDeleteConfirm}
            styles={!hasActiveLoan ? { primaryColor: '#fff', secondaryColor: theme.palette.error.main } : undefined}
            pageCall={{ category: 'PU - PAGOS', name: `ELIMINAR CUENTA${hasActiveLoan ? ' CON CRÉDITO ACTIVO' : ''}` }}
          />
        </>
        )}
    />
  );
}

ChangePacDialog.defaultProps = {
  onDeleteSubscription: undefined,
};
